
import { supabase } from "@/lib/supabase";

export interface PreviousAssessment {
  scores: Record<string, number>;
  overallScore: number;
  date: string;
  organizationName: string;
}

/**
 * Fetches the previous assessment for a user for a specific organization
 */
export const fetchPreviousAssessment = async (
  userEmail: string, 
  currentOrgName: string
): Promise<PreviousAssessment | null> => {
  if (!userEmail || !currentOrgName) return null;

  try {
    console.log("Fetching previous assessment for email:", userEmail, "and organization:", currentOrgName);
    
    // Fetch assessments for this organization
    const { data, error } = await supabase
      .from('assessments')
      .select('dimension_scores, overall_score, created_at, organization_details')
      .eq('email', userEmail)
      .order('created_at', { ascending: false })
      .limit(30); // Increased limit to find more potential assessments

    if (error) {
      console.error("Error fetching previous assessment:", error);
      throw error;
    }

    if (!data || data.length < 2) {
      // Need at least 2 entries to have a previous assessment
      console.log("Not enough assessments found for comparison");
      return null;
    }

    // Helper function to safely extract organization name
    const getOrgName = (orgDetails: any): string => {
      if (!orgDetails || typeof orgDetails !== 'object') {
        return '';
      }
      
      if ('name' in orgDetails && typeof orgDetails.name === 'string') {
        return orgDetails.name.trim().toLowerCase();
      }
      
      return '';
    };

    // Find assessments matching this organization
    const matchingAssessments = data.filter(assessment => {
      const orgName = getOrgName(assessment.organization_details);
      return orgName === currentOrgName.trim().toLowerCase();
    });

    console.log(`Found ${matchingAssessments.length} assessments for organization ${currentOrgName}`);

    // If we have more than one matching assessment, the second one is the "previous" one
    if (matchingAssessments.length > 1) {
      // Skip the first (most recent) and get the second one
      const prevAssessment = matchingAssessments[1];
      
      // Properly convert dimension_scores to Record<string, number>
      let dimensionScores: Record<string, number> = {};
      const rawScores = prevAssessment.dimension_scores;
      
      if (rawScores && typeof rawScores === 'object' && !Array.isArray(rawScores)) {
        Object.entries(rawScores).forEach(([key, value]) => {
          if (typeof value === 'number') {
            dimensionScores[key] = value;
          }
        });
      }
      
      // Extract organization name safely
      let orgName = currentOrgName;
      if (typeof prevAssessment.organization_details === 'object' && 
          prevAssessment.organization_details !== null) {
        // Safely access name property
        if ('name' in prevAssessment.organization_details) {
          const nameValue = prevAssessment.organization_details.name;
          if (typeof nameValue === 'string') {
            orgName = nameValue;
          }
        }
      }
      
      return {
        scores: dimensionScores,
        overallScore: prevAssessment.overall_score,
        date: prevAssessment.created_at,
        organizationName: orgName
      };
    }

    console.log("No previous assessment found for this organization");
    return null;
  } catch (err) {
    console.error('Error fetching previous assessment:', err);
    return null;
  }
};
